import { BigNumber } from '@ethersproject/bignumber';

import { IPairWallet } from 'api/apiPairs/models';
import { bnFrom } from 'tools/math';
import { IDexPair } from 'types/pairs';
import { formatToken } from 'utils/formats/formatToken';
import { web3Configs } from 'web3';

import { TransferRecipient, TransferTokenOption } from '../model/transferWizard.store';

export const getTransferTokenOptions = (pair: IDexPair): TransferTokenOption[] => {
  const options: TransferTokenOption[] = [];

  const pushUnique = (option: TransferTokenOption) => {
    if (options.find(o => o.id === option.id)) return;
    options.push(option);
  };

  pushUnique({
    id: pair.token_base.id,
    symbol: pair.token_base.symbol,
    decimals: pair.token_base.decimals,
    type: 'base',
  });

  pushUnique({
    id: pair.token_quote.id,
    symbol: pair.token_quote.symbol,
    decimals: pair.token_quote.decimals,
    type: 'quote',
  });

  pushUnique({
    id: pair.token_fee.id,
    symbol: pair.token_fee.symbol,
    decimals: pair.token_fee.decimals,
    type: 'native',
  });

  return options;
};

export const buildRecipientsFromWallets = ({
  wallets,
  token,
  previous,
}: {
  wallets: IPairWallet[];
  token: TransferTokenOption;
  previous?: TransferRecipient[];
}): TransferRecipient[] => {
  return wallets.map(wallet => {
    const balanceWei = getWalletTokenBalanceWei(wallet, token.id);
    const previousRecipient = previous?.find(recipient => recipient.walletId === wallet.id);

    return {
      walletId: wallet.id,
      walletAddress: wallet.address,
      balanceWei,
      balanceFormatted: formatToken(balanceWei, token.decimals),
      amount: previousRecipient?.amount ?? '',
    };
  });
};

export const getWalletTokenBalanceWei = (wallet: IPairWallet, tokenId: number): BigNumber => {
  if (!wallet.tokens) return BigNumber.from(0);

  const targetToken = wallet.tokens.find(token => token.token.id === tokenId);

  return bnFrom(targetToken?.balance ?? '0');
};

export const getWalletAvailableWei = ({
  wallet,
  token,
}: {
  wallet: IPairWallet;
  token: TransferTokenOption;
}): BigNumber => {
  return getWalletTokenBalanceWei(wallet, token.id);
};

export const getNetworkNativeToken = (pair: IDexPair): TransferTokenOption => {
  const native = web3Configs[pair.network].nativeCurrency;

  return {
    id: pair.token_fee.id,
    symbol: native.symbol,
    decimals: native.decimals,
    type: 'native',
  };
};
